import React, { useRef } from 'react';
import { Button, Input, Tooltip, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconSparkles, IconCamera } from '@tabler/icons-react';
import DictateButton from '../DictateButton';
import { SpeechDictation } from '../../hooks/useSpeechDictation';
import { IClarifyingQuestion } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text } = Typography;

/** Everything about this box that is copy rather than behaviour. */
export interface AssistantPromptCopy {
  titleKey: string;
  /** Shown before anything has been filled in. */
  subtitleKey: string;
  /** Replaces it once something has. */
  doneSubtitleKey: string;
  placeholderKey: string;
  /** The first press. */
  fillButtonKey: string;
  /** Any press after a draft has landed. */
  retryButtonKey: string;
  /** The press that sends the answers to the questions. */
  continueButtonKey: string;
}

interface AssistantPromptBoxProps {
  copy: AssistantPromptCopy;

  description: string;
  onDescriptionChange: (description: string) => void;
  /** The caller's own useSpeechDictation, so it can stop it before it sends. */
  dictation: SpeechDictation;

  /**
   * What the assistant made of it, in one line -- e.g.
   * "Excavadora de cadenas · Komatsu · PC210". Rendered only when there is
   * one, because it is the fastest way for somebody to catch the assistant
   * having understood the wrong thing: reading one line beats checking
   * twenty-five numbers.
   */
  recognised?: string;

  questions?: IClarifyingQuestion[];
  answers: Record<string, string>;
  onAnswer: (question: string, option: string) => void;

  working: boolean;
  /** Something has already been filled in, which changes the subtitle and the button. */
  done: boolean;
  disabled?: boolean;
  onSubmit: () => void;

  /**
   * Given a photo, when this box offers a camera at all -- today the
   * identification plate of a machine. Absent (accessories) and no camera
   * appears: an attachment rarely carries a plate worth reading.
   */
  onPhoto?: (photo: File) => void;
  /** The photo is being read, which is a different wait from the main one. */
  photoWorking?: boolean;
  /** What the camera button says it is for. */
  photoLabelKey?: string;
}

/**
 * The box that says "dime qué vas a publicar" -- the same one for a machine
 * and for an attachment.
 *
 * Purely what it looks like: a text area with a microphone, the one-line
 * summary of what was understood, up to a couple of tappable questions, and a
 * button. It owns no state and makes no calls, because the two things that
 * differ between a machine and an attachment are exactly those -- which
 * endpoints, and what to do when the taxonomy comes back empty.
 *
 * Extracted the second time it was needed rather than the first. Two copies of
 * a hundred lines of layout is how the accessory box quietly stops looking
 * like the machinery one after the third change to either.
 */
const AssistantPromptBox: React.FC<AssistantPromptBoxProps> = ({
  copy,
  description,
  onDescriptionChange,
  dictation,
  recognised,
  questions,
  answers,
  onAnswer,
  working,
  done,
  disabled,
  onSubmit,
  onPhoto,
  photoWorking,
  photoLabelKey,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  // A real <input type="file"> kept out of sight, because an antd Button is
  // the only thing here that looks like the rest of the app -- and because
  // `capture` is what makes a phone open the camera rather than the gallery.
  const photoInputRef = useRef<HTMLInputElement>(null);

  const hasQuestions = !!questions && questions.length > 0;

  return (
    <div style={{ marginBottom: 32, padding: 16, borderRadius: 16, background: TEAL_SOFT_BG }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <IconSparkles size={17} color={TEAL} style={{ flexShrink: 0 }} />
        <Text strong style={{ fontSize: 15 }}>{translate(t, copy.titleKey)}</Text>
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12, lineHeight: 1.45 }}>
        {translate(t, done ? copy.doneSubtitleKey : copy.subtitleKey)}
      </Text>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Input.TextArea
          rows={2}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={disabled || working}
          maxLength={1000}
          placeholder={translate(t, dictation.listening ? TRANSLATIONS.SERVICE_REQUEST_LISTENING : copy.placeholderKey)}
          style={{ borderRadius: 12, background: token.colorBgContainer }}
        />
        <DictateButton
          listening={dictation.listening}
          supported={dictation.supported}
          disabled={disabled || working}
          onStart={() => dictation.start(description)}
          onStop={dictation.stop}
        />
        {onPhoto && (
          <Tooltip title={photoLabelKey ? translate(t, photoLabelKey) : undefined}>
            <Button
              shape="round"
              icon={<IconCamera size={16} />}
              loading={photoWorking}
              disabled={disabled || working}
              aria-label={photoLabelKey ? translate(t, photoLabelKey) : undefined}
              onClick={() => photoInputRef.current?.click()}
            />
          </Tooltip>
        )}
      </div>

      {onPhoto && (
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          // Opens the rear camera straight away on a phone. Ignored on a
          // desktop browser, which shows the file picker it always would.
          capture="environment"
          style={{ display: 'none' }}
          onChange={(event) => {
            const photo = event.target.files?.[0];
            // Cleared either way, so photographing the same plate twice after
            // a bad read still fires a change event.
            event.target.value = '';
            if (photo) onPhoto(photo);
          }}
        />
      )}

      {recognised && (
        <Text style={{ fontSize: 12, display: 'block', marginTop: 10, color: TEAL }}>{recognised}</Text>
      )}

      {/* Only ever asked when the answer genuinely forks what this is --
          cadenas o ruedas, qué enganche lleva. Every one is skippable and
          "no lo sé" is always there, because a host who does not know is not
          a host who should be stuck. */}
      {hasQuestions && (
        <div style={{ marginTop: 14 }}>
          {questions!.map((question) => (
            <div key={question.question} style={{ marginBottom: 12 }}>
              <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>{question.question}</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[...question.options, translate(t, TRANSLATIONS.JOB_CLARIFIER_DONT_KNOW)].map((option) => (
                  <Button
                    key={option}
                    size="small"
                    shape="round"
                    type={answers[question.question] === option ? 'primary' : 'default'}
                    disabled={working}
                    onClick={() => onAnswer(question.question, option)}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Button
          type="primary"
          shape="round"
          icon={<IconSparkles size={15} />}
          loading={working}
          disabled={disabled}
          onClick={onSubmit}
        >
          {translate(t, hasQuestions ? copy.continueButtonKey : done ? copy.retryButtonKey : copy.fillButtonKey)}
        </Button>
      </div>
    </div>
  );
};

export default AssistantPromptBox;
